import { useState, useEffect } from "react";
import AdminSidebar from "../../components/AdminSidebar";
import { Database, Users, TrendingUp, X, Menu } from "lucide-react";
import { getUserStats } from "../../../services/userApi";
import { getModelStats } from "../../../services/modelApi";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { Drawer } from "../../components/ui/Drawer";

function Admin() {
  const [userStats, setUserStats] = useState(null);
  const [modelStats, setModelStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [showSidebar, setShowSidebar] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const [userStatsRes, modelStatsRes] = await Promise.all([
        getUserStats(),
        getModelStats(),
      ]);
      setUserStats(userStatsRes.data.stats);
      setModelStats(modelStatsRes.data.stats);
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error(error.message || "Failed to fetch dashboard statistics");
    } finally {
      setStatsLoading(false);
    }
  };

   const onClose = () => {
    setShowSidebar(false);
  };

  return (
    <div className="w-screen h-screen bg-gray-800 overflow-hidden">
      <div className="flex h-screen">
        {/* Sidebar */}
        <div className="hidden lg:block w-64 h-full">
          <AdminSidebar />
        </div>

          <Drawer
          height="full"
          title={"Admin"}
          isOpen={showSidebar}
          onClose={onClose}
        >
          <div className=" lg:hidden  w-full h-full">
            <AdminSidebar />
          </div>
        </Drawer>
        {/* Main Content */}
        <div className="flex-1 flex flex-col h-full overflow-y-auto bg-gray-800">
          {/* Header */}
          <div className="px-8 py-6  flex justify-between items-start">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-2xl  font-semibold text-white mb-1">
                  Admin Dashboard 
                </h1>
                <p className="text-gray-400 text-sm">
                  Overview of models and user states
                </p>
              </div>
              {!statsLoading && (
                <div className="flex gap-4 mt-4 md:mt-0">
                  <Link
                    role="button"
                    to={"/admin-dashboard/model-list"}
                    className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 flex items-center gap-2"
                  >
                    <Database className="text-blue-400" size={18} />
                    <span className="font-semibold text-white text-sm">
                      {statsLoading ? "..." : modelStats?.totalModels || 0}
                    </span>
                    <span className="text-gray-300 text-sm">Bots</span>
                  </Link>
                  <Link
                    role="button"
                    to={"/admin-dashboard/user-list"}
                    className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 flex items-center gap-2"
                  >
                    <Users className="text-green-400" size={18} />
                    <span className="font-semibold text-white text-sm">
                      {statsLoading ? "..." : userStats?.totalUsers || 0}
                    </span>
                    <span className="text-gray-300 text-sm">Users</span>
                  </Link>
                </div>
              )}
            </div>
            <div
              onClick={() => {
                setShowSidebar(true);
              }}
              className="max-w-max lg:hidden"
            >
              <Menu size={30} color="white" />
            </div>
          </div>

          {/* Dashboard Content */}
          <div className="flex-1 p-3 lg:p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl">
              {/* Models Card */}
              <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Database className="text-blue-400" size={24} />
                  <h2 className="text-lg font-semibold text-white">Bots</h2>
                </div>
                <div className="space-y-4">
                  {statsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg border border-gray-700">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-green-400"></div>
                          <span className="text-white font-medium text-sm">
                            Active Bots
                          </span>
                        </div>
                        <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full font-medium">
                          {modelStats?.activeModels || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg border border-gray-700">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-gray-500"></div>
                          <span className="text-white font-medium text-sm">
                            Inactive Bots
                          </span>
                        </div>
                        <span className="text-xs bg-gray-500/20 text-gray-400 px-2 py-1 rounded-full font-medium">
                          {modelStats?.inactiveModels || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg border border-gray-700">
                        <div className="flex items-center gap-3">
                          <TrendingUp className="text-blue-400" size={16} />
                          <span className="text-white font-medium text-sm">
                            Recent (7 days)
                          </span>
                        </div>
                        <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full font-medium">
                          {modelStats?.recentModels || 0}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Users Card */}
              <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Users className="text-green-400" size={24} />
                  <h2 className="text-lg font-semibold text-white">Users</h2>
                </div>
                <div className="space-y-4">
                  {statsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-400"></div>
                    </div>
                  ) : (
                    <>
                      <div className="flex  items-center justify-between p-3 bg-gray-800 rounded-lg border border-gray-700">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-green-400"></div>
                          <span className="text-white font-medium text-sm">
                            Active Users
                          </span>
                        </div>
                        <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full font-medium">
                          {userStats?.activeUsers || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg border border-gray-700">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                          <span className="text-white font-medium text-sm">
                            Admin Users
                          </span>
                        </div>
                        <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded-full font-medium">
                          {userStats?.adminUsers || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg border border-gray-700">
                        <div className="flex items-center gap-3">
                          <TrendingUp className="text-blue-400" size={16} />
                          <span className="text-white font-medium text-sm">
                            Recent (7 days)
                          </span>
                        </div>
                        <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full font-medium">
                          {userStats?.recentUsers || 0}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Admin;
